import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import AppHeader from '../components/ui/AppHeader';
import { useCRM } from '../utils/CRMContext';
import { db } from '../utils/firebase';
import {
  SalesActivityTimeRange,
  filterLeadsByRange,
  filterDealsByRange,
  buildLeadSourceBreakdown,
  buildSourceMetrics,
  buildTimeByLead,
  formatSalesActivityRangeLabel,
  formatSalesActivityRangeSubtitle,
} from '../utils/leadAnalytics';
import { buildCrmWinLossMetrics } from '../utils/salesWinLossAnalytics';
import {
  buildSalesActivityChangeOrders,
  buildSalesActivityTimeline,
  ChartMode,
  listSalesActivityForPeriod,
} from '../utils/salesActivityTimeline';
import type { ChangeOrder } from '../types';
import { useSalesWinLossDigests } from '../hooks/useSalesWinLossDigests';
import { useLeadTimeEntries } from '../hooks/useLeadTimeEntries';
import SalesActivityTimelineChart from '../components/sales-activity/SalesActivityTimelineChart';
import SalesActivityPeriodDetail from '../components/sales-activity/SalesActivityPeriodDetail';
import SourceBreakdownPiePanel, { SourceSlice } from '../components/sales-activity/SourceBreakdownPiePanel';
import LeadsBySourcePopover from '../components/sales-activity/LeadsBySourcePopover';
import TimePerLeadPopover from '../components/sales-activity/TimePerLeadPopover';
import WinLossBrainPanel from '../components/sales-activity/WinLossBrainPanel';
import '../components/pm/pm-shared.css';
import './FinancesHub.css';
import './SalesActivityHub.css';

const RANGES: SalesActivityTimeRange[] = ['7d', '30d', '90d', 'ytd', '1y', 'all'];
const MODES: { id: ChartMode; label: string }[] = [
  { id: 'leads', label: 'Leads' },
  { id: 'sales', label: 'Sales' },
  { id: 'both', label: 'Both' },
];

const fmtCurrency = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const SalesActivityPage: React.FC = () => {
  const { leads, enhancedDeals, pipelines } = useCRM();
  const [timeRange, setTimeRange] = useState<SalesActivityTimeRange>('90d');
  const [chartMode, setChartMode] = useState<ChartMode>('both');
  const [pipelineId, setPipelineId] = useState('');
  const [inspectedPeriodKey, setInspectedPeriodKey] = useState<string | null>(null);
  const [flyout, setFlyout] = useState<'source' | 'time' | null>(null);
  const [designFeePayments, setDesignFeePayments] = useState<
    { amount: number; paidAt: Date; dealId?: string }[]
  >([]);
  const [approvedChangeOrders, setApprovedChangeOrders] = useState<ChangeOrder[]>([]);
  const [changeOrdersError, setChangeOrdersError] = useState<string | null>(null);

  const { latestDigest, loading: digestLoading } = useSalesWinLossDigests();
  const { entries: leadTimeEntries } = useLeadTimeEntries(timeRange);

  useEffect(() => {
    const q = query(collection(db, 'clientPortals'), where('designFeePaid', '==', true));
    const unsub = onSnapshot(q, snapshot => {
      const payments: typeof designFeePayments = [];
      snapshot.docs.forEach(d => {
        const data = d.data();
        const projectsMap = (data.projects || {}) as Record<string, Record<string, unknown>>;
        for (const [projId, proj] of Object.entries(projectsMap)) {
          if (proj.designFeePaid && proj.amountPaid && proj.designFeePaidAt) {
            const paidAt =
              (proj.designFeePaidAt as { toDate?: () => Date })?.toDate?.() ||
              new Date(proj.designFeePaidAt as string);
            payments.push({
              amount: Number(proj.amountPaid) || 0,
              paidAt,
              dealId: projId,
            });
          }
        }
        if (payments.length === 0 && data.amountPaid && data.designFeePaidAt) {
          const paidAt =
            data.designFeePaidAt?.toDate?.() ||
            (data.designFeePaidAt ? new Date(data.designFeePaidAt) : null);
          if (paidAt) {
          payments.push({
              amount: Number(data.amountPaid) || 0,
            paidAt,
            dealId: data.dealId,
          });
          }
        }
      });
      setDesignFeePayments(payments);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'changeOrders'), where('status', '==', 'approved'));
    const unsub = onSnapshot(
      q,
      snap => {
        setChangeOrdersError(null);
        setApprovedChangeOrders(snap.docs.map(d => ({ id: d.id, ...(d.data() as ChangeOrder) })));
      },
      err => {
        console.error('changeOrders subscription failed:', err);
        setApprovedChangeOrders([]);
        setChangeOrdersError(
          err instanceof Error ? err.message : 'Could not load approved change orders',
        );
      },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    setInspectedPeriodKey(null);
  }, [timeRange, pipelineId]);

  const timelineChangeOrders = useMemo(
    () => buildSalesActivityChangeOrders(approvedChangeOrders, enhancedDeals),
    [approvedChangeOrders, enhancedDeals],
  );

  const filteredLeads = useMemo(
    () => filterLeadsByRange(leads, timeRange),
    [leads, timeRange],
  );
  const filteredDeals = useMemo(
    () => filterDealsByRange(enhancedDeals, timeRange, pipelineId || undefined),
    [enhancedDeals, timeRange, pipelineId],
  );

  const crmMetrics = useMemo(
    () => buildCrmWinLossMetrics(enhancedDeals, timeRange, pipelineId || undefined),
    [enhancedDeals, timeRange, pipelineId],
  );

  const timeline = useMemo(
    () =>
      buildSalesActivityTimeline({
        leads,
        deals: enhancedDeals,
        changeOrders: timelineChangeOrders,
        designFees: designFeePayments,
        range: timeRange,
        pipelineId: pipelineId || undefined,
      }),
    [leads, enhancedDeals, timelineChangeOrders, designFeePayments, timeRange, pipelineId],
  );

  const changeOrderRevenueInRange = useMemo(
    () => timeline.reduce((sum, point) => sum + (point.sales.changeOrderRevenue || 0), 0),
    [timeline],
  );
  const changeOrderCountInRange = useMemo(
    () => timeline.reduce((sum, point) => sum + (point.sales.changeOrderCount || 0), 0),
    [timeline],
  );

  const sourceMetrics = useMemo(
    () => buildSourceMetrics(filteredLeads, filteredDeals),
    [filteredLeads, filteredDeals],
  );
  const leadSource = useMemo(() => buildLeadSourceBreakdown(filteredLeads), [filteredLeads]);
  const timeByLead = useMemo(
    () => buildTimeByLead(leadTimeEntries, leads),
    [leadTimeEntries, leads],
  );

  const avgTimePerLead = useMemo(() => {
    const withTime = timeByLead.length;
    if (!withTime) return 0;
    const total = timeByLead.reduce((s, r) => s + r.totalMinutes, 0);
    return Math.round(total / withTime);
  }, [timeByLead]);

  const pieSlices = useMemo((): SourceSlice[] => {
    return leadSource.breakdown.map(b => ({
      source: b.source,
      count: b.count,
      color: b.color,
      percentage: b.percentage,
    }));
  }, [leadSource]);

  const inspectedDetail = useMemo(
    () =>
      inspectedPeriodKey
        ? listSalesActivityForPeriod({
            leads,
            deals: enhancedDeals,
            changeOrders: timelineChangeOrders,
            designFees: designFeePayments,
            periodKey: inspectedPeriodKey,
            pipelineId: pipelineId || undefined,
          })
        : null,
    [inspectedPeriodKey, leads, enhancedDeals, timelineChangeOrders, designFeePayments, pipelineId],
  );

  const togglePeriod = (periodKey: string) => {
    setInspectedPeriodKey(prev => (prev === periodKey ? null : periodKey));
  };

  return (
    <>
      <AppHeader />
      <div className="fin-hub sa-activity-hub">
        <div className="fin-hub-main">
          <div className="fin-hub-toolbar">
            <div className="fin-hub-toolbar-left">
              <h2 className="fin-hub-title">Sales &amp; Lead Activity</h2>
            {pipelines.length > 1 && (
              <select
                className="sa-pipeline-select"
                  value={pipelineId}
                  onChange={e => setPipelineId(e.target.value)}
                  aria-label="Pipeline"
              >
                  <option value="">All pipelines</option>
                {pipelines.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                ))}
              </select>
            )}
          </div>
            <div className="fin-hub-toolbar-right">
              <div className="fin-forecast-toggle" role="group" aria-label="Time range">
                {RANGES.map(r => (
                <button
                  key={r}
                    type="button"
                    className={timeRange === r ? 'active' : ''}
                  onClick={() => setTimeRange(r)}
                    aria-pressed={timeRange === r}
                    title={r === 'ytd' ? 'This calendar year' : r === '1y' ? 'Last 365 days' : undefined}
                >
                    {formatSalesActivityRangeLabel(r)}
                </button>
              ))}
            </div>
          </div>
        </div>

          <div className="fin-hub-content">
            <div className="fin-overview-grid">
              <button
                type="button"
                className="fin-kpi-card sa-kpi-clickable"
                onClick={() => setFlyout('source')}
              >
                <span className="fin-kpi-label">Leads</span>
                <span className="fin-kpi-value">{filteredLeads.length}</span>
                <span className="fin-kpi-sub">By source</span>
                </button>
              <button
                type="button"
                className="fin-kpi-card sa-kpi-clickable"
                onClick={() => setFlyout('time')}
              >
                <span className="fin-kpi-label">Avg time / lead</span>
                <span className="fin-kpi-value">
                  {avgTimePerLead >= 60 ? `${(avgTimePerLead / 60).toFixed(1)}h` : `${avgTimePerLead}m`}
                    </span>
                <span className="fin-kpi-sub">Time per lead</span>
              </button>
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">Close rate</span>
                <span className="fin-kpi-value">{crmMetrics.closeRate.toFixed(0)}%</span>
                <span className="fin-kpi-sub">Won vs closed in range</span>
                  </div>
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">Won revenue</span>
                <span className="fin-kpi-value">{fmtCurrency(crmMetrics.wonRevenue)}</span>
                <span className="fin-kpi-sub">Closed-won value</span>
              </div>
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">Change orders</span>
                <span className="fin-kpi-value">{fmtCurrency(changeOrderRevenueInRange)}</span>
                <span className="fin-kpi-sub">
                  {changeOrderCountInRange} approved in range
                  {changeOrdersError ? ' · load error' : ''}
                </span>
              </div>
        </div>
            {changeOrdersError && (
              <div className="sa-digest-banner" role="alert">
                Approved change orders could not be loaded ({changeOrdersError}). The sales
                timeline will omit change-order bars until this succeeds.
              </div>
            )}

            <div className="fin-forecast-chart-card">
              <div className="sa-chart-card-head">
                <h3>Activity over time</h3>
                <div className="sa-chart-card-controls">
                  <div className="fin-forecast-toggle" role="group" aria-label="Chart mode">
                    {MODES.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        className={chartMode === m.id ? 'active' : ''}
                        onClick={() => setChartMode(m.id)}
                        aria-pressed={chartMode === m.id}
                      >
                        {m.label}
                      </button>
                    ))}
                        </div>
                  <SourceBreakdownPiePanel
                    slices={pieSlices}
                    metrics={sourceMetrics}
                    title="All leads in range"
                    subtitle={`${filteredLeads.length} leads · ${formatSalesActivityRangeSubtitle(timeRange)}`}
                  />
                          </div>
                      </div>
              <SalesActivityTimelineChart
                data={timeline}
                mode={chartMode}
                formatCurrency={fmtCurrency}
                resetToken={`${timeRange}:${pipelineId}:${timeline[0]?.periodKey || ''}:${timeline.length}`}
                onPeriodClick={togglePeriod}
              />
        </div>

            {inspectedDetail && (
              <SalesActivityPeriodDetail
                detail={inspectedDetail}
                timeRange={timeRange}
                formatCurrency={fmtCurrency}
                onClose={() => setInspectedPeriodKey(null)}
              />
            )}

            {!digestLoading && !latestDigest && (
              <div className="sa-digest-banner">
                No win/loss brain digest yet — run <code>python -m sales.sync_patterns</code> on the Mac mini.
                </div>
            )}

            {latestDigest && (
              <WinLossBrainPanel digest={latestDigest} crmMetrics={crmMetrics} />
                )}
              </div>
                    </div>
              </div>

      {flyout === 'source' && (
        <LeadsBySourcePopover
          rows={sourceMetrics.length ? sourceMetrics : sourceMetricsFromBreakdown(leadSource)}
          timeRange={timeRange}
          onClose={() => setFlyout(null)}
        />
      )}
      {flyout === 'time' && (
        <TimePerLeadPopover rows={timeByLead} onClose={() => setFlyout(null)} />
      )}
    </>
  );
};

function sourceMetricsFromBreakdown(leadSource: ReturnType<typeof buildLeadSourceBreakdown>) {
  return leadSource.breakdown.map(b => ({
    source: b.source,
    leadCount: b.count,
    dealCount: 0,
    wonCount: 0,
    conversionRate: 0,
    winRate: 0,
    color: b.color,
  }));
}

export default SalesActivityPage;
